import type {
  JourneyContextVariableDefinition,
  JourneyContextVariableType,
} from "src/types/journeys";
import { isHttpLookupContextConfig } from "@hightouch/lib/journeys/types/config-types";
import {
  type ExecutableJourneyNode,
  isContextNode,
  isCouponAssignmentNode,
} from "src/pages/journeys/types";
import { buildCouponVarDefs } from "src/pages/journeys/forms/build-coupon-var-defs";

export const getKeyForContextVariable = (
  variableName: string,
  variableType: JourneyContextVariableType,
) => {
  return `${String(variableName ?? "").trim()}.${variableType}`;
};

export const getUsageByVariableName = (
  nodes: ExecutableJourneyNode[] | undefined,
  // Resolves the types of variables that HTTP lookup tiles set by name.
  variables: JourneyContextVariableDefinition[] = [],
): Record<string, number> => {
  const result: Record<string, number> = {};
  const variableTypes = new Map(
    variables.map((variable) => [
      variable.variable_name,
      variable.variable_type,
    ]),
  );

  (nodes ?? []).forEach((node) => {
    if (isContextNode(node) && isHttpLookupContextConfig(node.data.config)) {
      for (const mapping of node.data.config.lookup.mappings) {
        const type = variableTypes.get(mapping.variable_name);
        if (type) {
          const key = getKeyForContextVariable(mapping.variable_name, type);
          result[key] = (result[key] || 0) + 1;
        }
      }
    }

    // Get usage on context nodes
    if (isContextNode(node)) {
      node.data.config.values.forEach((value) => {
        const key = getKeyForContextVariable(
          value.variable_name,
          value.variable_type,
        );

        result[key] = (result[key] || 0) + 1;
      });
    }

    // Get usage on coupon assignment nodes
    if (isCouponAssignmentNode(node)) {
      const config = node.data.config;
      if (config.assignment_variable_name) {
        const key = getKeyForContextVariable(
          config.assignment_variable_name,
          "STRING",
        );
        result[key] = (result[key] || 0) + 1;
      }
      for (const col of config.additional_columns ?? []) {
        if (col.context_variable_name) {
          const key = getKeyForContextVariable(
            col.context_variable_name,
            col.context_variable_type,
          );
          result[key] = (result[key] || 0) + 1;
        }
      }
    }
  });

  return result;
};

/**
 * Maps each variable name owned by a coupon assignment node to its type.
 *
 * Two coupon assignment nodes on mutually exclusive branches may share a
 * variable so the destination gets one column instead of one sparse column per
 * branch. Sharing is only safe when both nodes agree on the type, since all
 * writers land in the same context log column — hence the type, not just the
 * name. Pass `excludeNodeId` to get the names owned by every *other* node.
 *
 * Ownership resolves through `buildCouponVarDefs`, the same function the form
 * save path uses to register declarations, so what a node counts as owning
 * here is exactly what it declared.
 */
export const getCouponAssignmentVarTypes = (
  nodes: ExecutableJourneyNode[] | undefined,
  excludeNodeId?: string,
): Map<string, JourneyContextVariableType> => {
  const types = new Map<string, JourneyContextVariableType>();
  (nodes ?? []).forEach((node) => {
    if (!isCouponAssignmentNode(node) || node.id === excludeNodeId) {
      return;
    }
    for (const def of buildCouponVarDefs(node.data.config).nodeVarDefs) {
      types.set(def.variable_name, def.variable_type);
    }
  });
  return types;
};

/**
 * Returns the set of variable names owned by coupon assignment nodes.
 * Used to prevent the Set Variable form from overwriting coupon-assigned variables.
 */
export const getCouponAssignmentVarNames = (
  nodes: ExecutableJourneyNode[] | undefined,
): Set<string> => new Set(getCouponAssignmentVarTypes(nodes).keys());

/**
 * The variable names that removing `removedNode` should undeclare: everything
 * it owns that no surviving coupon assignment node still uses.
 *
 * Since two nodes may share a variable, a name is only safe to undeclare once
 * the last node writing it is gone. Dropping one a survivor still references
 * would leave that node pointing at an undeclared variable, which the backend
 * rejects at save-time validation.
 *
 * `survivingNodes` must exclude every node being removed in the same
 * operation, not just `removedNode` — deleting a parent cascade-deletes its
 * branch nodes.
 */
export const getRemovableCouponVarNames = (
  removedNode: ExecutableJourneyNode,
  survivingNodes: ExecutableJourneyNode[],
): Set<string> => {
  const survivingNames = getCouponAssignmentVarNames(survivingNodes);
  return new Set(
    [...getCouponAssignmentVarNames([removedNode])].filter(
      (name) => !survivingNames.has(name),
    ),
  );
};

/** Returns the variable names set by Set variable tiles with static values. */
export const getStaticVarNames = (
  nodes: ExecutableJourneyNode[] | undefined,
): Set<string> => {
  const names = new Set<string>();
  for (const node of nodes ?? []) {
    if (isContextNode(node) && !isHttpLookupContextConfig(node.data.config)) {
      for (const value of node.data.config.values) {
        names.add(value.variable_name);
      }
    }
  }
  return names;
};

/**
 * Returns the variable names set by HTTP lookup Set variable tiles, other than
 * `excludeNodeId`. A lookup owns its variables like a coupon assignment does.
 */
export const getHttpLookupVarNames = (
  nodes: ExecutableJourneyNode[] | undefined,
  excludeNodeId?: string,
): Set<string> => {
  const names = new Set<string>();
  for (const node of nodes ?? []) {
    if (
      node.id !== excludeNodeId &&
      isContextNode(node) &&
      isHttpLookupContextConfig(node.data.config)
    ) {
      for (const mapping of node.data.config.lookup.mappings) {
        names.add(mapping.variable_name);
      }
    }
  }
  return names;
};
